import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Modal, Tag, Button } from "antd";
import { ArrowLeftOutlined, LockOutlined } from "@ant-design/icons";
import { FORMAT_OPTIONS, FEEDBACK_FORMAT, VOICE_DELIVERY_OPTIONS } from "./constants";
import FormatWaitlistButton from "./FormatWaitlistButton";
import styles from "./style.module.scss";

// Picking "AI voice survey" asks a second question: does the agent call people
// (outbound) or do they talk to it on a site or app (inbound)? onSelect(format, delivery).
const FormatSelectionModal = ({
  open,
  onClose,
  onSelect,
  waitlistJoined,
  waitlistLoading,
  onJoinWaitlist,
  capabilities,
  onUpgrade,
  startOnVoice = false,
}) => {
  const { t } = useTranslation();
  const canVoice = !!capabilities?.canVoice;
  const [voiceStep, setVoiceStep] = useState(false);

  useEffect(() => {
    if (open) setVoiceStep(!!startOnVoice);
  }, [open, startOnVoice]);

  const voiceDeliveryCards = (
    <>
      <button
        type="button"
        className={styles.formatBackBtn}
        onClick={() => setVoiceStep(false)}
      >
        <ArrowLeftOutlined /> {t("feedbackCapture.voice_delivery_back")}
      </button>
      <p className={styles.formatModalIntro}>
        {t("feedbackCapture.voice_delivery_intro")}
      </p>
      <div className={`${styles.formatCardGrid} ${styles.formatCardGridTwo}`}>
        {VOICE_DELIVERY_OPTIONS.map((option) => {
          const Icon = option.icon;
          return (
            <button
              key={option.key}
              type="button"
              className={styles.formatCard}
              onClick={() => onSelect(FEEDBACK_FORMAT.VOICE, option.key)}
            >
              <div className={styles.formatCardIconWrap}>
                <Icon className={styles.formatCardIcon} />
              </div>
              <div className={styles.formatCardBody}>
                <div className={styles.formatCardTitleRow}>
                  <h3 className={styles.formatCardTitle}>{t(option.titleKey)}</h3>
                  <Tag className={styles.formatCardSoon}>{t(option.tagKey)}</Tag>
                </div>
                <p className={styles.formatCardDesc}>{t(option.descKey)}</p>
              </div>
            </button>
          );
        })}
      </div>
    </>
  );

  return (
    <Modal
      title={t(voiceStep ? "feedbackCapture.voice_delivery_title" : "feedbackCapture.format_select_title")}
      open={open}
      onCancel={onClose}
      footer={null}
      width={720}
      destroyOnClose
      className={styles.formatModal}
    >
      {voiceStep ? (
        voiceDeliveryCards
      ) : (
        <>
          <p className={styles.formatModalIntro}>
            {t("feedbackCapture.format_select_intro")}
          </p>
          <div className={styles.formatCardGrid}>
            {FORMAT_OPTIONS.map((option) => {
              const Icon = option.icon;
              const isComingSoon = option.comingSoon;
              const voiceLocked =
                option.key === FEEDBACK_FORMAT.VOICE && !canVoice && !isComingSoon;
              const locked = isComingSoon || voiceLocked;
              const CardTag = locked ? "div" : "button";
              return (
                <CardTag
                  key={option.key}
                  type={locked ? undefined : "button"}
                  className={[
                    styles.formatCard,
                    locked ? styles.formatCardComingSoon : "",
                  ].join(" ")}
                  onClick={
                    locked
                      ? undefined
                      : () => {
                          if (option.key === FEEDBACK_FORMAT.VOICE) setVoiceStep(true);
                          else onSelect(option.key);
                        }
                  }
                >
                  <div className={styles.formatCardIconWrap}>
                    <Icon className={styles.formatCardIcon} />
                  </div>
                  <div className={styles.formatCardBody}>
                    <div className={styles.formatCardTitleRow}>
                      <h3 className={styles.formatCardTitle}>
                        {t(option.titleKey)}
                      </h3>
                      {isComingSoon ? (
                        <Tag className={styles.formatCardSoon}>
                          {t("feedbackCapture.format_coming_soon")}
                        </Tag>
                      ) : null}
                      {voiceLocked ? (
                        <Tag className={styles.formatCardSoon}>
                          {t("surveyManagement.survey_pro_badge", "Pro")}
                        </Tag>
                      ) : null}
                    </div>
                    <p className={styles.formatCardDesc}>{t(option.descKey)}</p>
                    {isComingSoon && option.waitlistFeature ? (
                      <FormatWaitlistButton
                        joined={!!waitlistJoined?.[option.waitlistFeature]}
                        loading={!!waitlistLoading?.[option.waitlistFeature]}
                        onJoin={() => onJoinWaitlist(option.waitlistFeature)}
                      />
                    ) : null}
                    {voiceLocked ? (
                      <div className={styles.formatCardDesc}>
                        <LockOutlined />{" "}
                        {t("feedbackCapture.format_voice_locked")}
                        {onUpgrade ? (
                          <Button
                            type="link"
                            size="small"
                            onClick={() => onUpgrade("pro")}
                          >
                            {t("surveyManagement.upgrade_plan_cta")}
                          </Button>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                </CardTag>
              );
            })}
          </div>
        </>
      )}
    </Modal>
  );
};

export default FormatSelectionModal;
